import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface UseAdminWordDeleteResult {
  deleteWord: (word: string) => Promise<boolean>;
  isDeleting: boolean;
}

export const useAdminWordDelete = (): UseAdminWordDeleteResult => {
  const [isDeleting, setIsDeleting] = useState(false);

  const deleteWord = async (word: string): Promise<boolean> => {
    setIsDeleting(true);
    try {
      // First find the word in the words table
      const { data: wordData, error: findError } = await supabase
        .from("words")
        .select("id")
        .eq("word_upper", word.toUpperCase())
        .maybeSingle();

      if (findError) {
        console.error("Error finding word:", findError);
        toast.error("Kunde inte hitta ordet i databasen");
        return false;
      }

      if (!wordData) {
        toast.error("Ordet hittades inte i databasen");
        return false;
      }

      // Delete all clues associated with this word
      const { error: cluesError } = await supabase
        .from("clues")
        .delete()
        .eq("word_id", wordData.id);

      if (cluesError) {
        console.error("Error deleting clues:", cluesError);
        toast.error("Kunde inte ta bort ledtrådar");
        return false;
      }

      // Delete the word itself
      const { error: wordError } = await supabase
        .from("words")
        .delete()
        .eq("id", wordData.id);

      if (wordError) {
        console.error("Error deleting word:", wordError);
        toast.error("Kunde inte ta bort ordet");
        return false;
      }

      toast.success(`"${word}" och alla tillhörande ledtrådar har tagits bort`);
      return true;
    } catch (error) {
      console.error("Error in deleteWord:", error);
      toast.error("Ett fel uppstod vid borttagning");
      return false;
    } finally {
      setIsDeleting(false);
    }
  };

  return { deleteWord, isDeleting };
};
